import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Combobox, Transition } from '@headlessui/react';
import {
    Search,
    Command,
    User,
    Building2,
    BookOpen,
    Plus,
    Settings,
    LogOut,
    FileText,
    Activity,
    Users,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CommandPalette = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const navigate = useNavigate();
    const { user, logout } = useAuth(); // Assuming logout is available in AuthContext

    // Toggle with Cmd+K or Ctrl+K
    useEffect(() => {
        const onKeydown = (e) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setIsOpen((open) => !open);
            }
        };

        window.addEventListener('keydown', onKeydown);
        return () => window.removeEventListener('keydown', onKeydown);
    }, []);

    const groups = [
        {
            name: 'Navigation',
            items: [
                { id: 'dashboard', name: 'Dashboard', icon: Activity, href: '/dashboard', roles: ['all'] },
                { id: 'colleges', name: 'Colleges', icon: Building2, href: '/colleges', roles: ['admin'] },
                { id: 'users', name: 'Users', icon: Users, href: '/users', roles: ['admin'] },
                { id: 'courses', name: 'Courses', icon: BookOpen, href: '/courses', roles: ['admin', 'college_admin'] },
            ],
        },
        {
            name: 'Quick Actions',
            items: [
                { id: 'add-college', name: 'Onboard New College', icon: Plus, href: '/colleges/onboard', roles: ['admin'] },
                { id: 'add-user', name: 'Add New User', icon: Plus, href: '/users', roles: ['admin'] }, // Redirects to list where modal likely is, or specific add page if exists
                { id: 'create-course', name: 'Create Course', icon: Plus, href: '/courses/create', roles: ['admin'] },
            ],
        },
        {
            name: 'Settings & Account',
            items: [
                { id: 'profile', name: 'My Profile', icon: User, href: `/users/${user?._id}`, roles: ['all'] },
                // { id: 'settings', name: 'Settings', icon: Settings, href: '/settings', roles: ['all'] },
                { id: 'logout', name: 'Logout', icon: LogOut, action: () => logout && logout(), roles: ['all'] },
            ],
        },
    ];

    // Filter items based on query and user role
    const filteredGroups = groups.map((group) => {
        const filteredItems = group.items.filter((item) => {
            // Role check
            const hasRole = item.roles.includes('all') || (user && item.roles.includes(user.role));
            if (!hasRole) return false;

            // Query check
            return item.name.toLowerCase().includes(query.toLowerCase());
        });

        return { ...group, items: filteredItems };
    }).filter(group => group.items.length > 0);

    const handleSelect = (item) => {
        setIsOpen(false);
        setQuery('');

        if (item.action) {
            item.action();
        } else if (item.href) {
            navigate(item.href);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment} afterLeave={() => setQuery('')}>
            <Dialog
                as="div"
                className="fixed inset-0 z-[9999] overflow-y-auto p-4 pt-[20vh]"
                onClose={setIsOpen}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <Combobox
                        as="div"
                        onChange={handleSelect}
                        className="relative mx-auto max-w-xl divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5"
                    >
                        <div className="flex items-center px-4">
                            <Search className="h-6 w-6 text-gray-500" aria-hidden="true" />
                            <Combobox.Input
                                className="h-12 w-full border-0 bg-transparent pl-4 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                                placeholder="Search command..."
                                onChange={(event) => setQuery(event.target.value)}
                                autoComplete="off"
                            />
                        </div>

                        {filteredGroups.length > 0 && (
                            <Combobox.Options static className="max-h-96 scroll-py-3 overflow-y-auto p-3">
                                {filteredGroups.map((group) => (
                                    <React.Fragment key={group.name}>
                                        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {group.name}
                                        </div>
                                        {group.items.map((item) => (
                                            <Combobox.Option
                                                key={item.id}
                                                value={item}
                                                className={({ active }) =>
                                                    `flex cursor-pointer select-none items-center rounded-xl px-3 py-2.5 mb-1 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900 hover:bg-gray-100'
                                                    }`
                                                }
                                            >
                                                {({ active }) => (
                                                    <>
                                                        <div className={`flex h-8 w-8 flex-none items-center justify-center rounded-lg ${active ? 'bg-indigo-500' : 'bg-gray-100'}`}>
                                                            <item.icon className={`h-5 w-5 ${active ? 'text-white' : 'text-gray-500'}`} aria-hidden="true" />
                                                        </div>
                                                        <span className="ml-3 flex-auto truncate text-sm font-medium">{item.name}</span>
                                                        {active && (
                                                            <span className="ml-3 flex-none text-indigo-100 text-xs">Jump to...</span>
                                                        )}
                                                    </>
                                                )}
                                            </Combobox.Option>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Combobox.Options>
                        )}

                        {query !== '' && filteredGroups.length === 0 && (
                            <div className="py-14 px-6 text-center text-sm sm:px-14">
                                <Command className="mx-auto h-6 w-6 text-gray-400" aria-hidden="true" />
                                <p className="mt-4 font-semibold text-gray-900">No results found</p>
                                <p className="mt-2 text-gray-500">No functionality found for "{query}". Please try again.</p>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center bg-gray-50 py-2.5 px-4 text-xs text-gray-500">
                            <span className="mx-1 flex h-5 w-5 items-center justify-center rounded border bg-white font-semibold">
                                <ArrowUp size={10} />
                            </span>
                            <span className="mx-1 flex h-5 w-5 items-center justify-center rounded border bg-white font-semibold">
                                <ArrowDown size={10} />
                            </span>
                            <span className="ml-1">to navigate</span>
                            <span className="mx-1 ml-3 flex h-5 w-10 items-center justify-center rounded border bg-white font-semibold">
                                Enter
                            </span>
                            <span className="ml-1">to select</span>
                            <span className="mx-1 ml-3 flex h-5 w-10 items-center justify-center rounded border bg-white font-semibold">
                                Esc
                            </span>
                            <span className="ml-1">to close</span>
                        </div>
                    </Combobox>
                </Transition.Child>
            </Dialog>
        </Transition.Root>
    );
};

export default CommandPalette;
